import { defineComponent, computed } from "vue";
import { mapState, mapActions } from "pinia";
import { Container } from "aurelia-dependency-injection";
import { useVuelidate } from "@vuelidate/core";
import { ReferralService } from "../../../services/referral-service";

import { ToastrService } from "../../../services/toastr.service";
import { useLoadingIndicator } from "../../Common/LoadingIndicator/store/useLoadingIndicator";
import { PatientService } from "../../../services/patient.service";
import { PalReferralTabs } from "../../../enums/PalReferralTabsEnum";
import { ApplicationEnum, HttpStatusCodeEnum } from "../../../enums/enums";
import { IPromptOptions } from "../../../resources-vue/vue-dialogs/prompt/prompt";
import { scrollToFirstError } from "../../../../src/utilities/scroll-to-first-error";
import { IOpenOrder } from "../../../resources-vue/vue-interfaces/i-plan-of-care";
import eventBus from "../../../utilities/eventBus";
import { useConvertToPendingPal } from "./store/useConvertToPendingPal";
import {
    PalliativeConvertToPending,
    PatientFuneralHome
} from "../../../resources-vue/vue-models/palliative-convert-to-pending";
import { LocationService } from "../../../services/location-service";
import { formatName } from "../../../common/format-name";

import PageLayout from "../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import Demographics from "./components/Demographics/Demographics.vue";
import Providers from "./components/Providers/Providers.vue";
import PayerInformation from "./components/PayerInformation/PayerInformation.vue";
import MciLayout from "../../../resources-vue/vue-elements/MciLayout/Mcilayout.vue";
import PharmacyAndDME from "./components/PharmacyAndDme/PharmacyAndDme.vue";
import ClinicalInformation from "./components/ClinicalInformation/ClinicalInformation.vue";
import Prompt from "../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import PatientGoals from "./components/PatientGoals/PatientGoals.vue";
import AttachPhysicianOrder from "../../../resources-vue/vue-custom-elements/AttachPhysicianOrder/AttachPhysicianOrder.vue";
import EmergencyPreparedness from "./components/EmergencyPreparedness/EmergencyPreparedness.vue";
import ReferralInformation from "./components/ReferralInformation/ReferralInformation.vue";
import AuthorizedContacts from "./components/AuthorizedContacts/AuthorizedContacts.vue";
import AdvanceDirectives from "./components/AdvanceDirectives/AdvanceDirectives.vue";
import HealthHistory from "./components/HealthHistory/HealthHistory.vue";
import TabsLayout from "../../../resources-vue/vue-custom-elements/TabsLayout/TabsLayout.vue";
import PatientNotes from "./components/PatientNotes/PatientNotes.vue";
import MyNotes from "./components/MyNotes/MyNotes.vue";

export enum ConvertToPalEvent {
    RequestNoteId = "convertToPal:requestNoteId"
}

export default defineComponent({
    components: {
        PageLayout,
        Demographics,
        Providers,
        MciLayout,
        PatientGoals,
        PharmacyAndDME,
        AttachPhysicianOrder,
        ClinicalInformation,
        EmergencyPreparedness,
        AuthorizedContacts,
        PayerInformation,
        ReferralInformation,
        AdvanceDirectives,
        HealthHistory,
        TabsLayout,
        PatientNotes,
        MyNotes
    },
    data(vm) {
        return {
            tabs: [
                { name: "Demographics", id: 1 },
                { name: "Payer Information", id: 2 },
                { name: "Clinical Information", id: 3 },
                { name: "Health History", id: 4 },
                { name: "Providers", id: 5 },
                { name: "Pharmacy and DME", id: 6 },
                { name: "Authorized Contacts", id: 7 },
                { name: "Emergency Preparedness", id: 8 },
                { name: "Advance Directives", id: 9 },
                { name: "Patient Goals", id: 10 },
                { name: "Referral Information", id: 11 },
                { name: "Patient Notes", id: 12 },
                { name: "My Notes", id: 13 }
            ],
            activeTab: 1,
            previousTab: 0,
            isFormLoading: true,
            isLoadError: false,
            isSaveInProgress: false,
            isNoteIdRequested: false,
            isAssessment: false,
            _toastrService: null,
            _patientService: null,
            _referralService: null,
            _serviceLocationService: null,
            contentWrapperPal: "",
            tabName: "",
            dialogRef: null,
            openOrders: [] as IOpenOrder[],
            noteId: "",
            urlNoteId: "",
            isAttachOrderNotRequired: false,
            v$: useVuelidate(),
            allErrors: [],
            previousTabs: [],
            formatName
        };
    },
    computed: {
        ...mapState(useConvertToPendingPal, [
            "patientDetails",
            "patientId",
            "accountId",
            "application",
            "patientOptions",
            "errors",
            "patientReferralId"
        ])
    },
    watch: {
        noteId() {
            if (this.noteId) {
                this.patientDetails.pharmacyAndDme.associatedTaskId = this.noteId;
            }
        },
        isAttachOrderNotRequired() {
            this.patientDetails.pharmacyAndDme.isAttachOrderNotRequired = this.isAttachOrderNotRequired;
        }
    },
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    async created() {
        this.resetAllErrors();
        this._toastrService = Container.instance.get(ToastrService);
        this._patientService = Container.instance.get(PatientService);
        this._serviceLocationService = Container.instance.get(LocationService);
        this._referralService = Container.instance.get(ReferralService);
        this.contentWrapperPal = "content-wrapper-pal";
        if (this.$route.params?.accountId) {
            this.setAccountId(this.$route.params.accountId as string);
        }
        if (this.$route.params?.patientId) {
            this.setPatientId(this.$route.params.patientId as string);
        }
        if (this.$route.params.patientReferralId) {
            this.setPatientReferralId(this.$route.params.patientReferralId as string);
        }
        await this.getSlimData();
        this.initPatient();
    },

    async mounted() {
        this.getPatientData(1);
        eventBus.on(ConvertToPalEvent.RequestNoteId, () => {
            this.isNoteIdRequested = true;
        });
    },

    methods: {
        ...mapActions(useConvertToPendingPal, {
            setPatientId: "SET_PATIENT_ID",
            setAccountId: "SET_ACCOUNT_ID",
            setPatientDetails: "SET_PATIENT_DETAILS",
            setApplication: "SET_APPLICATION",
            setPatientReferralId: "SET_PATIENT_REFERRAL_ID",
            setPatientOptions: "SET_PATIENT_OPTIONS",
            setErrors: "SET_ERRORS",
            resetErrors: "RESET_ERRORS",
            setNewErrors: "SET_NEW_ERRORS",
            resetAllErrors: "RESET_ALL_ERRORS"
        }),
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),

        getValidationDetails(tab: string, typeOfDetails: string) {
            switch (tab) {
                case "Demographics":
                    if (typeOfDetails === "numberOfErrors") {
                        return (
                            [...new Set(this.errors.demographics.errors)].length +
                            [...new Set(this.errors.primaryAddress)].length +
                            [...new Set(this.errors.visitAddress)].length +
                            [...new Set(this.errors.mailingAddress)].length +
                            [...new Set(this.errors.previousAddress)].length +
                            [...new Set(this.errors.serviceLocation)].length
                        );
                    } else {
                        return this.errors.demographics.isValidated;
                    }

                case "Payer Information":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.payerInformation.errors)].length;
                    } else {
                        return this.errors.payerInformation.isValidated;
                    }

                case "Referral Information":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.referralInformation.errors)].length;
                    } else {
                        return this.errors.referralInformation.isValidated;
                    }

                case "Clinical Information":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.clinicalInformation.errors)].length;
                    } else {
                        return this.errors.clinicalInformation.isValidated;
                    }

                case "Health History":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.healthHistory.errors)].length;
                    } else {
                        return this.errors.healthHistory.isValidated;
                    }

                case "Providers":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.providers.errors)].length;
                    } else {
                        return this.errors.providers.isValidated;
                    }

                case "Pharmacy and DME":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.pharmacyAndDme.errors)].length;
                    } else {
                        return this.errors.pharmacyAndDme.isValidated;
                    }

                case "Authorized Contacts":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.authorizedContacts.errors)].length;
                    } else {
                        return this.errors.authorizedContacts.isValidated;
                    }

                case "Emergency Preparedness":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.emergencyPreparedness.errors)].length;
                    } else {
                        return this.errors.emergencyPreparedness.isValidated;
                    }

                case "Advance Directives":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.advanceDirectives.errors)].length;
                    } else {
                        return this.errors.advanceDirectives.isValidated;
                    }

                case "Patient Goals":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.patientGoals.errors)].length;
                    } else {
                        return this.errors.patientGoals.isValidated;
                    }

                case "Patient Notes":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.patientNotes.errors)].length;
                    } else {
                        return this.errors.patientNotes.isValidated;
                    }

                case "My Notes":
                    if (typeOfDetails === "numberOfErrors") {
                        return [...new Set(this.errors.myNotes.errors)].length;
                    } else {
                        return this.errors.myNotes.isValidated;
                    }
            }
        },

        async handleTabChange(tab: any) {
            this.previousTab = this.activeTab;
            this.activeTab = tab.id;
            if (this.previousTab !== 7) {
                this.saveOnTabChange(this.previousTab);
            } else {
                const payload = {
                    property: this.getStoreName(this.previousTab),
                    value: []
                } as any;
                this.setNewErrors(payload, true, false);
            }
            this.getPatientData(this.activeTab);
        },

        async getPatientData(tab: number) {
            this.tabName = PalReferralTabs.getDescriptionUsingValue(tab);
            try {
                this.showLoading(true);
                this.isFormLoading = true;
                const result = await this._patientService.getConvertPalliativeReferral({
                    patientId: this.patientId,
                    agencyId: this.accountId,
                    application: this.application,
                    tabName: this.tabName
                });

                if (!this.errors.demographics.hasErrors && tab == 1) {
                    this.patientDetails.demographics = result?.demographics;
                } else if (!this.errors.payerInformation.hasErrors && tab == 2) {
                    this.patientDetails.payerInformation = result?.payerInformation;
                } else if (!this.errors.clinicalInformation.hasErrors && tab == 3) {
                    this.patientDetails.clinicalInformation = result?.clinicalInformation;
                } else if (!this.errors.healthHistory.hasErrors && tab == 4) {
                    this.patientDetails.healthHistory = result?.healthHistory;
                } else if (!this.errors.providers.hasErrors && tab == 5) {
                    this.patientDetails.providers = result?.providers;
                } else if (!this.errors.pharmacyAndDme.hasErrors && tab == 6) {
                    this.patientDetails.pharmacyAndDme = result?.pharmacyAndDme;
                } else if (!this.errors.authorizedContacts.hasErrors && tab == 7) {
                    this.patientDetails.authorizedContacts = result?.authorizedContacts;
                } else if (!this.errors.emergencyPreparedness.hasErrors && tab == 8) {
                    this.patientDetails.emergencyPreparedness = result?.emergencyPreparedness;
                } else if (!this.errors.advanceDirectives.hasErrors && tab == 9) {
                    this.patientDetails.advanceDirectives = result?.advanceDirectives;
                    if (!this.patientDetails.advanceDirectives?.patientFuneralHome) {
                        this.patientDetails.advanceDirectives.patientFuneralHome = new PatientFuneralHome();
                        this.patientDetails.advanceDirectives.patientFuneralHome.id =
                            "00000000-0000-0000-0000-000000000000";
                    }
                } else if (!this.errors.patientGoals.hasErrors && tab == 10) {
                    this.patientDetails.patientGoals = result?.patientGoals;
                } else if (!this.errors.referralInformation.hasErrors && tab == 11) {
                    this.patientDetails.referralInformation = result?.referralInformation;
                } else if (!this.errors.patientNotes.hasErrors && tab == 12) {
                    this.patientDetails.patientNotes = result?.patientNotes;
                } else if (!this.errors.myNotes.hasErrors && tab == 13) {
                    this.patientDetails.myNotes = result?.myNotes;
                } else {
                    this.patientDetails = result;
                }
                this.setPatientDetails(new PalliativeConvertToPending(this.patientDetails));
                this.showLoading(false);
                this.isFormLoading = false;
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting this referral. Please try again. If the problem persists, please report it.`
                });
            }
        },

        async getSlimData() {
            try {
                this.isLoadError = true;
                const response = await this._patientService.getSlimData(this.patientId, {
                    agencyId: this.accountId,
                    application: this.application
                });
                this.isLoadError = false;
                if (response.statusCode == 200 && response.data) {
                    this.setPatientOptions(response);
                } else {
                    this._toastrService.error({
                        title: `Error`,
                        message: `${response.message}`
                    });
                }
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting patient data. Please try again. If the problem persists, please report it.`
                });
            }
        },

        async checkFuenralHome() {
            if (!!this.patientDetails.advanceDirectives?.patientFuneralHome) {
                let valid = Object.values(this.patientDetails.advanceDirectives?.patientFuneralHome).some(
                    (value) => !!value && value !== "00000000-0000-0000-0000-000000000000"
                );
                if (!valid) {
                    this.patientDetails.advanceDirectives.patientFuneralHome = null;
                }
            }
        },

        async validateServiceLocation(tabId: number) {
            if (tabId == 1) {
                let serviceLocationValidation = await this.serviceLocationCheck();
                if (!serviceLocationValidation) {
                    let payload = {
                        property: "demographics",
                        error: "name"
                    };
                    this.setErrors(payload, true, true);
                    this._toastrService.error({
                        title: `Error`,
                        message: `A Service Location is required.`
                    });
                    return;
                }
            }
        },

        async validatePrimaryPayer(tabId: number) {
            if (tabId == 2) {
                let payerValidation = await this.patientPayersCheck();
                if (!payerValidation) {
                    let payload = {
                        property: "payerInformation",
                        error: "payer"
                    };
                    this.setErrors(payload, true, true);
                    this._toastrService.error({
                        title: `Error`,
                        message: `Primary payer is required`
                    });
                    return;
                }
            }
        },

        async save(tabId: any) {
            let addressNotValidated = this.checkLatAndLong();
            if (addressNotValidated) {
                this.validateAddressMessage();
                return;
            }
            let isModelValid = await this.v$.$validate();
            let tabName = PalReferralTabs.getDescriptionUsingValue(tabId);
            this.v$.$errors.map((val) => {
                let property;
                if (val.$propertyPath.split(".").length === 5) {
                    property = val.$propertyPath.split(".")[1];
                    const payload = {
                        property: property,
                        error: val.$property
                    };

                    this.setErrors(payload, true, true);
                } else {
                    property =
                        val.$propertyPath.split(".").length === 3
                            ? val.$propertyPath.split(".")[1]
                            : val.$propertyPath.split(".").length === 2
                              ? val.$message
                              : val.$propertyPath.split(".")[1];
                    let payload = {
                        property: property,
                        error: val.$property
                    };

                    this.setErrors(payload, true, true);
                }
            });

            await this.validateServiceLocation(tabId);
            await this.validatePrimaryPayer(tabId);

            if (isModelValid) {
                const newErrors = {
                    property: this.getStoreName(tabId),
                    value: []
                } as any;
                this.setNewErrors(newErrors, true, false);
                try {
                    this.showLoading(true);
                    await this.checkFuenralHome();
                    let payloadData = this.patientDetails;

                    if (tabId == 10) {
                        const patientGoal = {
                            patientGoal: { ...this.patientDetails.patientGoals }
                        };
                        payloadData.patientGoals = {} as any;
                        Object.assign(payloadData.patientGoals, patientGoal);
                    }
                    const response = await this._patientService.updateConvertPalliativeReferral({
                        payload: payloadData,
                        accountId: this.accountId,
                        patientId: this.patientId,
                        application: ApplicationEnum.AxxessPalliative,
                        tabName: tabName
                    });
                    if (response.Status == 200) {
                        this.getPatientData(this.activeTab);
                        this._toastrService.success({
                            title: "Patient Updated",
                            message: `The patient's ${tabName} has been successfully updated.`
                        });
                    } else {
                        if (response.Status == 400) {
                            if (response.errors.length > 0) {
                                this._toastrService.error({
                                    title: `Valdation Error`,
                                    message: `${response.errors.map((error: any) => error.message)}`
                                });
                                this.handleSetResponseErrors(tabId, response);
                            } else if (response.ErrorMessage) {
                                this._toastrService.error({
                                    title: `Valdation Error`,
                                    message: response.ErrorMessage
                                });
                                this.handleSetResponseError(tabId, response.ErrorMessage);
                            }
                        } else if (response.Status == 304) {
                            this._toastrService.info({
                                title: "The patient is up to date",
                                message: `There were no changes made in ${tabName}`
                            });
                            const payload = {
                                property: this.getStoreName(tabId),
                                value: []
                            } as any;
                            this.setNewErrors(payload, true, false);
                        } else {
                            const payload = {
                                property: this.getStoreName(tabId),
                                value: []
                            } as any;
                            this.setNewErrors(payload, true, false);
                        }
                    }
                } catch (err) {
                    console.log(err);
                    if (err.status === HttpStatusCodeEnum.NotModified) {
                        this._toastrService.info({
                            title: `The patient is up to date`,
                            message: `There were no changes made in ${this.patientDetails.demographics.firstName} ${this.patientDetails.demographics.lastName}'s profile.`
                        });
                    } else {
                        this._toastrService.error({
                            title: `Error`,
                            message: `The patient's ${tabName} could not be saved.`
                        });
                    }
                } finally {
                    this.showLoading(false);
                    if (!this.previousTabs.includes(this.previousTab)) {
                        this.previousTabs.push(this.previousTab);
                    }
                }
            } else {
                scrollToFirstError();
                this._toastrService.error({
                    title: `Error`,
                    message: `Please fill the all required fields`
                });
            }
        },

        async complete() {
            this.showLoading(true);
            for (const tab of this.tabs) {
                if (tab.id == 7) {
                    const payload = {
                        property: this.getStoreName(tab.id),
                        value: []
                    } as any;
                    this.setNewErrors(payload, true, false);
                } else {
                    if (tab.id === 13) {
                        await this.save(tab.id);
                    }
                    let tabName = PalReferralTabs.getDescriptionUsingValue(tab.id);
                    if (!this.previousTabs.includes(tab.id) && tab.id !== 13) {
                        try {
                            this.showLoading(true);
                            await this.validateServiceLocation(tab.id);
                            await this.validatePrimaryPayer(tab.id);

                            await this.getPatientData(tab.id);
                            await this.checkFuenralHome();
                            let payloadData = this.patientDetails;

                            if (tab.id == 10) {
                                const patientGoal = {
                                    patientGoal: { ...this.patientDetails.patientGoals }
                                };
                                payloadData.patientGoals = {} as any;
                                Object.assign(payloadData.patientGoals, patientGoal);
                            }
                            const response = await this._patientService.updateConvertPalliativeReferral({
                                payload: payloadData,
                                accountId: this.accountId,
                                patientId: this.patientId,
                                application: ApplicationEnum.AxxessPalliative,
                                tabName: tabName
                            });
                            if (response.Status == 200) {
                                this._toastrService.success({
                                    title: "Patient Updated",
                                    message: `The patient's ${tabName} has been successfully updated.`
                                });

                                const payload = {
                                    property: this.getStoreName(tab.id),
                                    value: []
                                } as any;
                                this.setNewErrors(payload, true, false);
                            } else if (response.Status == 400) {
                                if (response.errors.length > 0) {
                                    this._toastrService.error({
                                        title: `Valdation Error`,
                                        message: `${response.errors.map((error: any) => error.message)}`
                                    });
                                    this.handleSetResponseErrors(tab.id, response);
                                } else if (response.ErrorMessage) {
                                    this._toastrService.error({
                                        title: `Valdation Error`,
                                        message: response.ErrorMessage
                                    });
                                    this.handleSetResponseError(tab.id, response.ErrorMessage);
                                }
                            } else {
                                const payload = {
                                    property: this.getStoreName(tab.id),
                                    value: []
                                } as any;
                                this.setNewErrors(payload, true, false);
                            }
                        } catch (err) {
                            console.log(err);
                            if (err.status === HttpStatusCodeEnum.NotModified) {
                                this._toastrService.info({
                                    title: `The patient is up to date`,
                                    message: `There were no changes made in ${this.patientDetails.demographics.firstName} ${this.patientDetails.demographics.lastName}'s profile.`
                                });
                            } else {
                                this._toastrService.error({
                                    title: `Error`,
                                    message: `The patient's ${tabName} could not be saved.`
                                });
                            }
                        }
                    }
                }
            }
            let hasError = this.tabs.some((tab) => this.errors[this.getStoreName(tab.id)].hasErrors);
            if (!hasError) {
                const requestBody = {
                    id: this.patientReferralId,
                    query: {
                        id: this.patientReferralId,
                        application: Number(this.application),
                        accountId: this.accountId
                    }
                };
                const responseData = await this._referralService.palConvertToPending(requestBody);
                if (responseData.isSuccess) {
                    this._toastrService.success({
                        title: "Success",
                        message: `You have successfully updated the patient`
                    });
                    this.resetAllErrors();
                    this.$router.back();
                } else {
                    this._toastrService.error({
                        title: "Error",
                        message: `${responseData.message}`
                    });
                    this.showLoading(false);
                }
            } else {
                this._toastrService.error({
                    title: `Error`,
                    message: `Please complete all required fields.`
                });
                this.showLoading(false);
            }
        },

        async saveOnTabChange(tabId: any) {
            let tabName = PalReferralTabs.getDescriptionUsingValue(tabId);
            if (!this.previousTabs.includes(this.previousTab)) {
                this.previousTabs.push(this.previousTab);
            }
            try {
                this.showLoading(true);
                await this.validateServiceLocation(tabId);
                await this.validatePrimaryPayer(tabId);

                await this.checkFuenralHome();
                let payloadData = this.patientDetails;

                if (tabId == 10) {
                    const patientGoal = {
                        patientGoal: { ...this.patientDetails.patientGoals }
                    };
                    payloadData.patientGoals = {} as any;
                    Object.assign(payloadData.patientGoals, patientGoal);
                }

                const response = await this._patientService.updateConvertPalliativeReferral({
                    payload: payloadData,
                    accountId: this.accountId,
                    patientId: this.patientId,
                    application: ApplicationEnum.AxxessPalliative,
                    tabName: tabName
                });
                if (response.Status == 200) {
                    this._toastrService.success({
                        title: "Patient Updated",
                        message: `The patient's ${tabName} has been successfully updated.`
                    });
                    const payload = {
                        property: this.getStoreName(tabId),
                        value: []
                    } as any;
                    this.setNewErrors(payload, true, false);
                } else {
                    if (response.Status == 400) {
                        if (response.errors.length > 0) {
                            this._toastrService.error({
                                title: `Valdation Error`,
                                message: `${response.errors.map((error: any) => error.message)}`
                            });
                            this.handleSetResponseErrors(tabId, response);
                        } else if (response.ErrorMessage) {
                            this._toastrService.error({
                                title: `Valdation Error`,
                                message: response.ErrorMessage
                            });
                            this.handleSetResponseError(tabId, response.ErrorMessage);
                        }
                    } else {
                        const payload = {
                            property: this.getStoreName(tabId),
                            value: []
                        } as any;
                        this.setNewErrors(payload, true, false);
                    }
                }
            } catch (err) {
                console.log(err);
                if (err.status === HttpStatusCodeEnum.NotModified) {
                    this._toastrService.info({
                        title: `The patient is up to date`,
                        message: `There were no changes made in ${this.patientDetails.demographics.firstName} ${this.patientDetails.demographics.lastName}'s profile.`
                    });
                } else {
                    this._toastrService.error({
                        title: `Error`,
                        message: `The patient's ${tabName} could not be saved.`
                    });
                }
            } finally {
                this.showLoading(false);
            }
        },

        handleSetResponseErrors(tabId: number, response: any) {
            switch (tabId) {
                case 1:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "demographics",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 2:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "payerInformation",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 3:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "clinicalInformation",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 4:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "healthHistory",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 5:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "providers",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 6:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "pharmacyAndDme",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 7:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "authorizedContacts",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 8:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "emergencyPreparedness",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 9:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "advanceDirectives",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 10:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "patientGoals",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 11:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "referralInformation",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 12:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "patientNotes",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
                case 13:
                    response.errors.map((error: any) => {
                        const payload = {
                            property: "myNotes",
                            error: error.message
                        };
                        this.setErrors(payload, true, true);
                    });
                    break;
            }
        },

        handleSetResponseError(tabId: number, message: string) {
            switch (tabId) {
                case 1:
                    this.storeSingleResponseErrorMessage("demographics", message);
                    break;
                case 2:
                    this.storeSingleResponseErrorMessage("payerInformation", message);
                    break;
                case 3:
                    this.storeSingleResponseErrorMessage("clinicalInformation", message);
                    break;
                case 4:
                    this.storeSingleResponseErrorMessage("healthHistory", message);
                    break;
                case 5:
                    this.storeSingleResponseErrorMessage("providers", message);
                    break;
                case 6:
                    this.storeSingleResponseErrorMessage("pharmacyAndDme", message);
                    break;
                case 7:
                    this.storeSingleResponseErrorMessage("authorizedContacts", message);
                    break;
                case 8:
                    this.storeSingleResponseErrorMessage("emergencyPreparedness", message);
                    break;
                case 9:
                    this.storeSingleResponseErrorMessage("advanceDirectives", message);
                    break;
                case 10:
                    this.storeSingleResponseErrorMessage("patientGoals", message);
                    break;
                case 11:
                    this.storeSingleResponseErrorMessage("referralInformation", message);
                    break;
                case 12:
                    this.storeSingleResponseErrorMessage("patientNotes", message);
                    break;
                case 13:
                    this.storeSingleResponseErrorMessage("myNotes", message);
                    break;
            }
        },

        storeSingleResponseErrorMessage(property: string, message: string) {
            const payload = {
                property,
                error: message
            };
            this.setErrors(payload, true, true);
        },

        checkLatAndLong() {
            return (
                !this.patientDetails.demographics.primaryAddress.isValidated &&
                !this.patientDetails.demographics.primaryAddress.latitude &&
                !this.patientDetails.demographics.primaryAddress.longitude &&
                !this.patientDetails.demographics?.primaryAddress?.isBypassed
            );
        },

        validateAddressMessage() {
            this.isFormLoading = false;
            this._toastrService.error({
                title: "Error",
                message: "Click to validate patient's primary address."
            });
        },

        async serviceLocationCheck() {
            try {
                let serviceLocationData = await this._serviceLocationService.getServiceLocations(this.patientId, {
                    page: 1,
                    pageLength: 1,
                    application: this.application,
                    accountId: this.accountId
                });
                return serviceLocationData.itemCount > 0;
            } catch (e) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was an error while updating the patient. Please try again.`
                });
                return false;
            }
        },

        async patientPayersCheck() {
            const query = {
                page: 1,
                pageLength: 10,
                sortBy: "",
                order: "desc",
                application: this.application,
                accountId: this.accountId,
                payorTypes: "Primary"
            };
            let payerData = await this._patientService.getPatientPayers(query, this.patientId);
            return payerData.data.ItemCount > 0;
        },

        async next() {
            this.previousTab = this.activeTab;
            this.activeTab = this.activeTab + 1;
            if (this.previousTab !== 7) {
                await this.saveOnTabChange(this.previousTab);
            } else {
                const payload = {
                    property: this.getStoreName(this.previousTab),
                    value: []
                } as any;
                this.setNewErrors(payload, true, false);
            }

            this.getPatientData(this.activeTab);
        },
        async previous() {
            this.previousTab = this.activeTab;
            this.activeTab = this.activeTab - 1;
            if (this.previousTab !== 7) {
                await this.saveOnTabChange(this.previousTab);
            } else {
                const payload = {
                    property: this.getStoreName(this.previousTab),
                    value: []
                } as any;
                this.setNewErrors(payload, true, false);
            }
            this.getPatientData(this.activeTab);
        },

        getStoreName(tabId: number) {
            switch (tabId) {
                case 1:
                    return "demographics";

                case 2:
                    return "payerInformation";

                case 3:
                    return "clinicalInformation";

                case 4:
                    return "healthHistory";

                case 5:
                    return "providers";

                case 6:
                    return "pharmacyAndDme";

                case 7:
                    return "authorizedContacts";

                case 8:
                    return "emergencyPreparedness";

                case 9:
                    return "advanceDirectives";

                case 10:
                    return "patientGoals";

                case 11:
                    return "referralInformation";

                case 12:
                    return "patientNotes";

                case 13:
                    return "myNotes";
                default:
                    return "demographics";
            }
        },

        async cancel() {
            let promptOptions: IPromptOptions = {
                message:
                    "Do you want to leave the page? This patient will remain on the Referral List and your updates will not be saved.",
                okText: "Stay Here",
                cancelText: "Leave Page"
            };
            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: (options) => {
                    if (options.data !== "success") {
                        this.resetAllErrors();
                        this.$router.back();
                    }
                }
            });
        },

        async initPatient() {
            let payload = {
                accountId: this.accountId,
                application: this.application
            };
            this.openOrders = await this._patientService.getOpenOrders(this.patientId, payload);
            if (this.$route.params?.noteId) {
                this.noteId = String(this.$route.params?.noteId);
                this.urlNoteId = String(this.$route.params?.noteId);
            }
            if (this.$route.params?.isAssessment === "true") {
                this.isAssessment = Boolean(this.$route.params?.isAssessment);
            }
            if (this.$route.params?.noteId && !this.isAssessment) {
                let isValidOrderLinked = this.openOrders.find((order) => order.OrderId == this.$route.params?.noteId);
                if (isValidOrderLinked) {
                    this.isNoteIdRequested = true;
                } else {
                    this._toastrService.error({
                        title: `Error - Order not found`,
                        message: `There was a problem while loading the Selected Physician Order. Please try again.`
                    });
                }
            }
        },
        undoChanges() {
            if (this.urlNoteId) {
                this.$router.push(`/note/${this.noteId}`);
            } else {
                this.noteId = "";
                this.patientDetails.pharmacyAndDme.associatedTaskId = "";
            }
        },
        closeUpdateNoteId() {
            this.isNoteIdRequested = false;
        }
    }
});
